import React, { useEffect, useState } from 'react';
import { Form, Button, Card } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import Message from '../components/Message';
import Loader from '../components/Loader';
import Meta from '../components/Meta';
import { getOrderDetails, submitCancelRequest, resetOrderFlags } from '../slices/orderSlice';

const OrderCancelScreen = ({ match, history }) => {
  const orderId = match.params.id;
  const dispatch = useDispatch();

  const [reason, setReason] = useState('');
  const [description, setDescription] = useState('');
  const [localError, setLocalError] = useState('');

  const orderState = useSelector((state) => state.order);
  const {
    orderDetails: order,
    loading,
    error,
    loadingCancelRequest,
    successCancelRequest,
  } = orderState;

  useEffect(() => {
    if (!orderId) return;
    if (!order?._id || String(order._id) !== String(orderId)) {
      dispatch(getOrderDetails(orderId));
    }
  }, [dispatch, orderId, order?._id]);

  useEffect(() => {
    if (successCancelRequest) {
      dispatch(resetOrderFlags());
      history.push(`/order/${orderId}`);
    }
  }, [dispatch, history, orderId, successCancelRequest]);

  const cancellationStatus = String(order?.cancellationRequest?.status || 'none');
  const notEligible = Boolean(order?.isProcessed || order?.isShipped || order?.isDelivered);

  const submitHandler = (e) => {
    e.preventDefault();
    setLocalError('');

    const r = String(reason || '').trim();
    const d = String(description || '').trim();

    if (!r) {
      setLocalError('Please provide a reason.');
      return;
    }
    if (notEligible) {
      setLocalError('Cancellation is no longer eligible for this order.');
      return;
    }
    if (cancellationStatus === 'pending') {
      setLocalError('A cancellation request is already pending for this order.');
      return;
    }

    dispatch(submitCancelRequest({ orderId, reason: r, description: d }));
  };

  return (
    <>
      <Meta title='Cancel Order' robots='noindex,follow' />
      <h1>Cancel Order</h1>

      {loading ? (
        <Loader />
      ) : error ? (
        <Message variant='danger'>{error}</Message>
      ) : order?.status === 'cancelled' ? (
        <Message variant='info'>
          This order has already been cancelled.
          <div className='mt-3'>
            <Button variant='secondary' onClick={() => history.push(`/order/${orderId}`)}>
              Back to Order
            </Button>
          </div>
        </Message>
      ) : (
        <Card className='p-3'>
          {localError && <Message variant='danger'>{localError}</Message>}

          {notEligible && (
            <Message variant='warning'>
              Cancellation is no longer eligible because this order has been processed/shipped/delivered.
            </Message>
          )}

          {cancellationStatus === 'pending' && (
            <Message variant='info'>A cancellation request is already pending for this order.</Message>
          )}

          <Form onSubmit={submitHandler}>
            <Form.Group controlId='cancelReason'>
              <Form.Label>Reason (required)</Form.Label>
              <Form.Control
                as='select'
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                disabled={notEligible || cancellationStatus === 'pending'}
              >
                <option value=''>Select a reason</option>
                <option value='Changed my mind'>Changed my mind</option>
                <option value='Ordered by mistake'>Ordered by mistake</option>
                <option value='Found a better price'>Found a better price</option>
                <option value='Other'>Other</option>
              </Form.Control>
            </Form.Group>

            <Form.Group controlId='cancelDescription' className='mt-3'>
              <Form.Label>Description (optional)</Form.Label>
              <Form.Control
                as='textarea'
                rows={4}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={notEligible || cancellationStatus === 'pending'}
                placeholder='Add any helpful details (optional)'
              />
            </Form.Group>

            <div className='mt-3 d-flex align-items-center gap-2'>
              <Button
                type='submit'
                variant='danger'
                disabled={loadingCancelRequest || notEligible || cancellationStatus === 'pending'}
              >
                Submit Cancellation Request
              </Button>
              {loadingCancelRequest && <Loader />}
              <Button
                type='button'
                variant='secondary'
                onClick={() => history.push(`/order/${orderId}`)}
              >
                Back
              </Button>
            </div>
          </Form>
        </Card>
      )}
    </>
  );
};

export default OrderCancelScreen;
